import { Component, OnInit } from '@angular/core';
import { FormGroup, FormBuilder, Validators } from "@angular/forms";
import { DatePipe } from "@angular/common";
import { BehaviorSubject, Observable, of, forkJoin } from "rxjs";
import { catchError, tap, switchMap, map } from "rxjs/operators";
import { ColDef, GridOptions, GridApi } from "ag-grid-community";

import { GridFormatterService } from "../../../../common/services/grid-formatter.service";
import { SubHeaderService } from "../../../../core/services/sub-header.service";
import { SubHeader } from "../../../../core/models/sub-header.model";
import { DynamicDialogService } from "../../../../core/services/dynamic-dialog.service";
import { Dialog, DialogButton } from "../../../../core/models/dialog.model";
import { DateService } from "../../../../common/services/date.service";
import { AdhocReportsService } from "../../../services/adhoc-reports-service";
import { ContactHistorySearchSpec, ContactService, ContactSearchSpec } from "../../../services/contacts.service";
import { ContactHistoryEvent, ContactEventType } from "../../../models/contactHistory.model";
import { Contact, ContactStub } from "../../../models/contact.model";
import { OrganizationService } from "../../../../common/services/organization.service";
import { OrganizationStub } from "../../../../common/models/organization.model";

@Component({
    selector: 'app-user-permissions-report',
    templateUrl: './user-permissions-report.component.html',
    styleUrls: ['./user-permissions-report.component.scss'],
    standalone: false
})
export class UserPermissionsReportComponent implements OnInit {

    private subheader = new SubHeader({ title: "User Permissions Report", buttons: [{ name: "Run Report", method: () => this.runReport() }, { name: "Export", method: () => this.export() }] });

    get allowExport(): boolean {
        return this.dataLoaded.value;
    }

    contactData: Contact[];

    data: Observable<ContactHistoryEvent[]>;

    dataLoaded: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    isFlashing: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    excelStyles = this.formatter.excelStyles;

    get flashing(): boolean {
        return this.isFlashing.value;
    }

    form: FormGroup;

    gridOptions: GridOptions;
    gridApi: GridApi;

    invalidDatesDialog: Dialog = new Dialog("When providing dates, both From and To dates must be provided, and the To date must be greater than or equal to the From date.", [new DialogButton("OK", false, () => { return false; })])


    notReadyDialog: Dialog = new Dialog("Report must finish loading before it can be exported.", [new DialogButton("OK", false, () => { return false; })]);

    organizationData: OrganizationStub[];

    get fromDate(): Date {
        let from = this.form.get('fromDate').get('datePicker').value;
        return from;
    }

    get toDate(): Date {
        let to = this.form.get('toDate').get('datePicker').value;
        if (to != null) to.setHours(23, 59, 59, 59);
        return to;
    }

    get dateValidation(): boolean {
        if (this.fromDate == null || this.toDate == null) return false;
        return this.fromDate < this.toDate;
    }

    get fromDateValid(): boolean {
        let ctrl = this.form.get('fromDate');
        if (ctrl == null) return false;
        ctrl = ctrl.get('datePicker');
        if (ctrl == null) return false;
        return ctrl.valid;
    }

    get toDateValid(): boolean {
        let ctrl = this.form.get('toDate');
        if (ctrl == null) return false;
        ctrl = ctrl.get('datePicker');
        if (ctrl == null) return false;
        return ctrl.valid;
    }

    constructor(private builder: FormBuilder, private formatter: GridFormatterService, private subheaderService: SubHeaderService, private dialogService: DynamicDialogService,
        private dateService: DateService, private reportsService: AdhocReportsService, private contactService: ContactService, private organizationService: OrganizationService, private pipe: DatePipe) { }

    ngOnInit() {
        this.subheaderService.updateSubHeader(this.subheader);
        this.buildForm();
        this.gridOptions = this.formatter.BuildDefaultGridOptionsWithColumns(this.createColumnDefinitions(), false, true, true, false);
        this.gridOptions.headerHeight = 50;
        this.gridOptions.onGridReady = (params) => {
			this.gridApi = params.api;
            this.gridApi.sizeColumnsToFit();
		};
        this.runReport();
    }

    buildForm(): void {
        let from: Date = new Date()
        from.setDate(from.getDate() - 30);
        from.setHours(0, 0, 0, 0);
        let to: Date = new Date();
        to.setHours(23, 59, 59, 59);
        this.form = this.builder.group({
            fromDate: this.builder.group({ datePicker: [from, [Validators.required]] }),
            toDate: this.builder.group({ datePicker: [to, [Validators.required]] })
        });
        this.form.addControl("dateGreaterThan", this.builder.control(this.dateValidation, [Validators.requiredTrue]));
    }

    contactFormatter(field: string): any {
        return (obj: any) => {
            let name: string = "Unknown";
            if (obj != null && obj.data != null && obj.data["contactId"] != null) {
                let match = this.contactData.find(c => c.id === obj.data["contactId"]);
                if (match != null) {
                    name = match[field];
                }
            }
            return name;
        };
    }

    createColumnDefinitions(): ColDef[] {
        return [
            {
                headerClass: "wrapped",
                headerName: "Event Time",
                headerTooltip: "Event Time",
                field: "timestamp",
                filter: "agDateColumnFilter",
                filterParams: this.formatter.defaultDateFilterParams,
                valueFormatter: this.formatter.formatDate("timestamp", "MM/dd/yyyy hh:mm:ss a"),
                tooltipValueGetter: this.formatter.formatDate("timestamp", "MM/dd/yyyy hh:mm:ss a"),
                minWidth: 175,
                maxWidth: 175
            } as ColDef,
            {
                headerClass: "wrapped",
                headerName: "User Name",
                headerTooltip: "User Name",
                field: "name",
                filterParams: this.formatter.defaultTextFilterParams,
                filter: "agTextColumnFilter",
                valueGetter: this.contactFormatter("displayName"),
                tooltipValueGetter: this.contactFormatter("displayName"),
                minWidth: 150,
                maxWidth: 200
            } as ColDef,
            {
                headerClass: "wrapped",
                headerName: "Email Address",
                headerTooltip: "Email Address",
                field: "name",
                filterParams: this.formatter.defaultTextFilterParams,
                filter: "agTextColumnFilter",
                valueGetter: this.contactFormatter("emailAddress"),
                tooltipValueGetter: this.contactFormatter("emailAddress"),
                minWidth: 150,
                maxWidth: 200
            } as ColDef,
            {
                headerClass: "wrapped",
                headerName: "Organization",
                headerTooltip: "Organization",
                field: "organizationId",
                filter: "agTextColumnFilter",
                filterParams: this.formatter.defaultTextFilterParams,
                valueGetter: this.organizationFormatter(),
                tooltipValueGetter: this.organizationFormatter(),
                minWidth: 125,
                maxWidth: 250
            },
            {
                headerClass: "wrapped",
                headerName: "Internal Contact",
                headerTooltip: "Internal Contact",
                filter: "agTextColumnFilter",
                filterParams: this.formatter.defaultTextFilterParams,
                valueGetter: this.internalContactFormatter(),
                tooltipValueGetter: this.internalContactFormatter(),
                minWidth: 125,
                maxWidth: 150
            },
            {
                headerClass: "wrapped",
                headerName: "Event",
                headerTooltip: "Event",
                field: "eventType",
                valueGetter: this.eventFormatter("eventType"),
                tooltipValueGetter: this.eventFormatter("eventType"),
                filter: "agTextColumnFilter",
                filterParams: this.formatter.defaultTextFilterParams,
                minWidth: 200,
                maxWidth: 200
            } as ColDef,
            {
                headerClass: "wrapped",
                headerName: "Event Details",
                headerTooltip: "Event Details",
                field: "eventDetails",
                tooltipField: "eventDetails",
                filter: " agTextColumnFilter",
                filterParams: this.formatter.defaultTextFilterParams,
                minWidth: 200
            }
        ] as ColDef[];
    }

    eventFormatter(field: any): any {
        return (obj: any) => {
            let value: any = "Unknown";
            if (obj.data != null && obj.data[field] != null) {
                value = obj.data[field];
                switch (value) {
                    case ContactEventType.AgentTypeChanged:
                        value = "Agent Type Changed";
                        break;
                    case ContactEventType.ContactCanLoginChanged:
                        value = "Contact Can Login Changed";
                        break;
                    case ContactEventType.ContactCreation:
                        value = "Contact Created";
                        break;
                    case ContactEventType.ContactUpdated:
                        value = "Contact Updated";
                        break;
                }
            }
            return value;
        }
    }

    export(): void {
        if (this.allowExport) {
            let fromDate = this.form.get('fromDate').get('datePicker').value;
            if (fromDate != null) fromDate.setHours(0, 0, 0, 0);
            let toDate = this.form.get('toDate').get('datePicker').value;
            if (toDate != null) toDate.setHours(23, 59, 59, 59);
            let fileName = 'UserPermissions';
            if (fromDate != null) fileName = `${fileName}_FROM_${this.pipe.transform(fromDate, "yyyy-MM-dd")}`;
            if (toDate != null) fileName = `${fileName}_TO_${this.pipe.transform(toDate, "yyyy-MM-dd")}`;
            const sheetName = 'UserPermissions';
            this.formatter.exportDataToExcel(fileName, sheetName, this.gridApi);
        }
        else {
            this.notReadyDialog.toggleDialog(true);
            this.dialogService.updateDialog(this.notReadyDialog);
        } 
    }

    internalContactFormatter(): any {
        return (obj) => {
            let name: string = "Unknown";
            if (obj != null && obj.data != null && obj.data["contactId"] != null) {
                let match = this.contactData.find(c => c.id === obj.data["contactId"]);
                if (match != null) name = match.internalContact ? "Yes" : "No";
            }
            return name;
        };
    }

    onDateUpdated(evt: any): void {
        this.form.get('dateGreaterThan').setValue(this.dateValidation);
        this.form.get('dateGreaterThan').updateValueAndValidity();
    }

    organizationFormatter(): any {
        return (obj: any) => {
            let name: string = "Unknown";
            if (obj != null && obj.data != null && obj.data["contactId"] != null) {
                let contact = this.contactData.find(c => c.id === obj.data["contactId"]);
                if (contact != null) {
                    let match = this.organizationData.find(c => c.id === contact.organizationId);
                    if (match != null) name = match.name;
                }
            }            
            return name;
        };
    }

    runReport(): void {
        this.form.get('dateGreaterThan').setValue(this.dateValidation);
        this.form.get('dateGreaterThan').updateValueAndValidity();
        if (this.form.valid) {
            this.dataLoaded.next(false);
            let fromDate = this.form.get('fromDate').get('datePicker').value;
            if (fromDate != null) fromDate.setHours(0, 0, 0, 0);
            let toDate = this.form.get('toDate').get('datePicker').value;
            if (toDate != null) toDate.setHours(23, 59, 59, 59);
            let spec = {
                startDate: fromDate,
                endDate: toDate,
                eventTypes: [
                    ContactEventType.AgentTypeChanged,
                    ContactEventType.ContactCreation,
                    ContactEventType.ContactCanLoginChanged,
                    ContactEventType.ContactUpdated
                ]
            } as ContactHistorySearchSpec;
            this.isFlashing.next(true);
            setTimeout(() => { this.isFlashing.next(false); }, 500);

            let contactObs$: Observable<Contact[]> = (this.contactData != null && this.contactData.length > 0)
                ? of(this.contactData)
                : this.contactService.getAll({ stubs: false } as ContactSearchSpec).pipe(
                    tap((contacts: Contact[]) => {
                        this.contactData = contacts;
                    })
                );
            let organizationObs$: Observable<OrganizationStub[]> = (this.organizationData != null && this.organizationData.length > 0)
                ? of(this.organizationData)
                : this.organizationService.getAll().pipe(
                    tap((organizations: OrganizationStub[]) => {
                        this.organizationData = organizations;
                    })
                )
            

            this.data = forkJoin([contactObs$, organizationObs$]).pipe(
                switchMap(() => {
                    return this.reportsService.getUserPermissionsReport(spec).pipe(
                        map((events: ContactHistoryEvent[]) => {
                            return events.filter(h => h.eventDetails.toLocaleLowerCase().includes("log in") || h.eventDetails.toLocaleLowerCase().includes("group") || h.eventDetails.toLocaleLowerCase().includes("creation"));
                        })
                    )
                }),
                catchError((error) => {
                    console.error(error);
                    this.dialogService.showError();
                    return [];
                }),                
                tap((data) => {
                    this.dataLoaded.next(true);
                })
            );
    } else {
            this.dialogService.showFormInvalid();
        }
    }
}
